import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../../../components/footer/Footer";
import styles from "../../../styles/__login.module.scss";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import axios from "axios";
import { postLogin } from "../../../reducer/login/loginReducer";
import Illustration from "../../../assets/images/physioLogin.png";
import { RiEyeCloseLine, RiEyeLine } from "react-icons/ri";

const PhysioLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    // Username validation
    if (!username.trim()) {
      newErrors.username = "Username is required";
    } else if (username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    // Password validation
    if (!password.trim()) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/[a-z]/.test(password)) {
      newErrors.password =
        "Password must include at least one lowercase letter";
    } else if (!/[A-Z]/.test(password)) {
      newErrors.password =
        "Password must include at least one uppercase letter";
    } else if (!/[0-9]/.test(password)) {
      newErrors.password = "Password must include at least one number";
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      newErrors.password =
        "Password must include at least one special character";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const passwordRules = {
    length: {
      test: (pwd) => pwd.length >= 8,
      message: "At least 8 characters",
    },
    lowercase: {
      test: (pwd) => /[a-z]/.test(pwd),
      message: "At least one lowercase letter",
    },
    uppercase: {
      test: (pwd) => /[A-Z]/.test(pwd),
      message: "At least one uppercase letter",
    },
    number: {
      test: (pwd) => /[0-9]/.test(pwd),
      message: "At least one number",
    },
    special: {
      test: (pwd) => /[!@#$%^&*(),.?\":{}|<>]/.test(pwd),
      message: "At least one special character",
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    console.log("Physiotherapist login attempt:", {
      username,
      role_id: 8
    });
    
    const loginData = {
      username: username,
      password: password,
      role_id: 8  // Hardcoded for Physiotherapist role
    };

    const result = await dispatch(postLogin(loginData));
    if (result && result?.payload) {
      // Save tokens
      sessionStorage.setItem(
        "usr_1a2b3c",
        result?.payload?.data?.access_token
      );
      sessionStorage.setItem(
        "usr_4d5e6f",
        result?.payload?.data?.refresh_token
      );
      sessionStorage.setItem(
        "usr_role",
        result?.payload?.data?.user?.role_id
      );
      // Store role_id for route protection
      if (result?.payload?.data?.user?.role_id) {
        sessionStorage.setItem("usr_role_id", result?.payload?.data?.user?.role_id.toString());
      }
      sessionStorage.setItem(
        "usr_name",
        result?.payload?.data?.user?.username
      );

      // Mandatory onboarding gate: a physio center must complete its profile +
      // add at least one default address before using the app. We detect
      // completion by checking for a default address; the result is cached in
      // sessionStorage so route guards can enforce it without re-fetching.
      const accessToken = result?.payload?.data?.access_token;
      try {
        const addressRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/v1/physiotherapist/address`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const addresses = addressRes?.data?.data || [];
        if (Array.isArray(addresses) && addresses.some((a) => a?.is_default)) {
          sessionStorage.setItem("usr_physio_onboarded", "true");
          navigate("/physio_dashboard");
        } else {
          sessionStorage.setItem("usr_physio_onboarded", "false");
          navigate("/physio_onboarding");
        }
      } catch (err) {
        // If the check fails, don't block login — send to the dashboard.
        sessionStorage.setItem("usr_physio_onboarded", "true");
        navigate("/physio_dashboard");
      }
    }
  };

  return (
    <div className={styles["main-container"]}>
      {/* Main Content */}
      <main className={styles["main-content"]}>
        <div className={styles["content-wrapper"]}>
          {/* Doctor Illustration */}
          <div className={styles["illustration"]}>
            <div className={styles["doctor-illustration"]}>
              <img
                src={Illustration}
                alt="Physiotherapist illustration"
                className={styles["doctor-image"]}
              />
            </div>
          </div>

          {/* Login Form */}
          <div className={styles["login-form-container"]}>
            <h2>{t("loginToAccount")}</h2>
            <form onSubmit={handleSubmit} className={styles["login-form"]}>
              {/* Username */}
              <div className={styles["form-group"]}>
                <label htmlFor="username">
                  Username<span style={{ color: "#EF4444" }}> *</span>
                </label>
                <input
                  type="text"
                  id="username"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={styles["form-input"]}
                />
                {errors?.username && (
                  <p className={styles["rule-invalid"]}>{errors?.username}</p>
                )}
              </div>

              {/* Password */}
              <div className={styles["form-group"]}>
                <label htmlFor="password">
                  {t("password")}<span style={{ color: "#EF4444" }}> *</span>
                </label>
                <div className={styles["password-wrapper"]}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={styles["form-input"]}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={styles["password-toggle"]}
                  >
                    {showPassword ? (
                      <RiEyeLine size={20} />
                    ) : (
                      <RiEyeCloseLine size={20} />
                    )}
                  </button>
                </div>
                {errors?.password && (
                  <p className={styles["rule-invalid"]}>{errors?.password}</p>
                )}
                {/* <div className={styles["password-rules"]}>
                  {Object.keys(passwordRules).map((ruleKey) => {
                    const rule = passwordRules[ruleKey];
                    const isValid = rule.test(password);
                    return (
                      <p
                        key={ruleKey}
                        className={

                          isValid
                            ? styles["rule-valid"]
                            : styles["rule-invalid"]
                        }
                      >
                        {isValid ? "✔" : "✖"} {rule.message}
                      </p>
                    );
                  })}
                </div> */}
                <p className={styles["Forgot-pwd-link"]}>
                  <Link to="/forgot-password">{t("forgotPassword")}?</Link>
                </p>
              </div>

              {/* Login Button */}
              <button type="submit" className={styles["login-btn"]}>
                {t("login")}
              </button>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PhysioLogin;
